import { UserActions, UserState, ItransformedData, OrganizationOption, AuthInfo } from '@/types/user';
// 플러그인은 plugins/pinia.ts에서 전역 등록됨
import { defineStore } from 'pinia';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_APP_API_URL || 'http://localhost:8080';

export interface UserStore extends UserState, UserActions {}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    isLogin: false,
    userToken: '',
    refreshToken: '',
    accessTokenExpiresAt: '',
    refreshTokenExpiresAt: '',
    userEmail: '',
    userName: '',
    userGivenName: '',
    userFamilyName: '',
    userPicture: '',
    userMenu: [],
    memId: '',
    userType: '',
    memGrade: '',
    memGradeName: '',
    deptGroupA: '',
    deptGroupAName: '',
    deptGroupB: '',
    deptGroupBName: '',
    bbdgId: '',
    bbdgIdName: '',
    // 소속 정보 옵션들
    regionOptions: [],
    departmentOptions: [],
    branchOptions: [],
    // view_path별 권한 정보 맵
    authByViewPath: {},
  }),
  getters: {
    getUserMenu(): UserState['userMenu'] {
      return this.userMenu;
    },
    // 메뉴별 권한 정보 조회
    getMenuAuthType(): (viewPath: string) => string | undefined {
      return (viewPath: string) => {
        const findAuthType = (menus: any[]): string | undefined => {
          for (const menu of menus) {
            if (menu.view_path === viewPath) {
              return menu.auth_type;
            }
            if (menu.children && menu.children.length > 0) {
              const found = findAuthType(menu.children);
              if (found) return found;
            }
          }
          return undefined;
        };
        return findAuthType(this.userMenu);
      };
    },
  },

  actions: {
    setIsLogin(status: boolean) {
      this.isLogin = status;
    },
    setUserToken(token: string) {
      this.userToken = token;
      if (typeof window !== 'undefined')
        token ? localStorage.setItem('access_token', token) : localStorage.removeItem('access_token');
    },
    setRefreshToken(refreshToken: string) {
      this.refreshToken = refreshToken;
    },
    setTokenExpiresAt(accessTokenExpiresAt: string, refreshTokenExpiresAt: string) {
      this.accessTokenExpiresAt = accessTokenExpiresAt;
      this.refreshTokenExpiresAt = refreshTokenExpiresAt;
    },
    setUserEmail(email: string) {
      this.userEmail = email;
      if (typeof window !== 'undefined')
        email ? localStorage.setItem('user_email', email) : localStorage.removeItem('user_email');
    },
    setUserName(userName: string) {
      this.userName = userName;
    },
    setUserGivenName(userGivenName: string) {
      this.userGivenName = userGivenName;
    },
    setUserFamilyName(userFamilyName: string) {
      this.userFamilyName = userFamilyName;
    },
    setUserPicture(userPicture: string) {
      this.userPicture = userPicture;
    },
    setUserMenu(userMenu: ItransformedData[]) {
      this.userMenu = userMenu;
    },
    setMemId(memId: string) {
      this.memId = memId;
    },
    setUserType(userType: string) {
      this.userType = userType;
    },
    setMemGrade(memGrade: string, memGradeName: string) {
      this.memGrade = memGrade;
      this.memGradeName = memGradeName;
    },
    setOrganizationInfo(deptGroupA: string, deptGroupAName: string, deptGroupB: string, deptGroupBName: string, bbdgId: string, bbdgIdName: string) {
      this.deptGroupA = deptGroupA;
      this.deptGroupAName = deptGroupAName;
      this.deptGroupB = deptGroupB;
      this.deptGroupBName = deptGroupBName;
      this.bbdgId = bbdgId;
      this.bbdgIdName = bbdgIdName;
    },
    setOrganizationOptions(
    regionOptions: OrganizationOption[],
    departmentOptions: OrganizationOption[],
    branchOptions: OrganizationOption[]
  ) {
    this.regionOptions = regionOptions;
    this.departmentOptions = departmentOptions;
    this.branchOptions = branchOptions;
  },
    // view_path별 권한 정보 설정
    setAuthByViewPath(viewPath: string, authInfo: AuthInfo) {
      this.authByViewPath[viewPath] = authInfo;
    },
    // view_path별 권한 정보 조회
    getAuthByViewPath(viewPath: string): AuthInfo | null {
      return this.authByViewPath[viewPath] || null;
    },
    
    // Refresh Token으로 Access Token 갱신
    async refreshAccessToken(): Promise<boolean> {
      try {
        if (!this.refreshToken) {
          console.error('🔍 [UserStore] Refresh token이 없습니다');
          this.clearStore();
          return false;
        }

        // Refresh token 만료 체크
        if (this.refreshTokenExpiresAt && new Date() >= new Date(this.refreshTokenExpiresAt)) {
          console.error('🔍 [UserStore] Refresh token이 만료되었습니다');
          this.clearStore();
          return false;
        }
        
        const response = await axios.post(`${API_BASE_URL}/api/refresh`, {
          refresh_token: this.refreshToken
        });

        const { jwt, refresh_token, access_token_expires_at, refresh_token_expires_at } = response.data;

        // 새 토큰들로 업데이트
        this.setUserToken(jwt);
        this.setRefreshToken(refresh_token);
        this.setTokenExpiresAt(access_token_expires_at, refresh_token_expires_at);

        return true;
      } catch (error: any) {
        console.error('❌ [UserStore] 토큰 갱신 실패:', error);
        
        // 401 에러인 경우 상세 메시지 출력
        if (error.response?.status === 401) {
          console.error('🔍 [UserStore] 401 에러 - Refresh token이 유효하지 않음:', error.response?.data?.error);
          console.error('🔍 [UserStore] 서버 재시작으로 인한 토큰 유실 가능성 - 다시 로그인이 필요합니다');
        }
        
        this.clearStore();
        return false;
      }
    },

    // 로그아웃
    async logout(): Promise<void> {
      try {
        if (this.refreshToken) {
          await axios.post(`${API_BASE_URL}/api/logout`, {
            refresh_token: this.refreshToken
          });
        }
      } catch (error) {
        console.error('로그아웃 API 호출 실패:', error);
      } finally {
        this.clearStore();
      }
    },

    // Access Token 만료 여부 확인
    isAccessTokenExpired(): boolean {
      if (!this.accessTokenExpiresAt) return true;
      return new Date() >= new Date(this.accessTokenExpiresAt);
    },

    // Refresh Token 만료 여부 확인  
    isRefreshTokenExpired(): boolean {
      if (!this.refreshTokenExpiresAt) return true;
      return new Date() >= new Date(this.refreshTokenExpiresAt);
    },
    initializeStore() {
      // 플러그인 사용 시 수동 복원 불필요. 호환성을 위해 남겨둠.
    },
    // 저장된 사용자 정보 로깅
    logStoredUserInfo() {
      console.log('📋 [UserStore] ===== 저장된 사용자 정보 =====');
      console.log('🔐 인증 정보:', {
        isLogin: this.isLogin,
        userToken: this.userToken ? `${this.userToken.substring(0, 20)}...` : '',
        refreshToken: this.refreshToken ? `${this.refreshToken.substring(0, 20)}...` : '',
        accessTokenExpiresAt: this.accessTokenExpiresAt,
        refreshTokenExpiresAt: this.refreshTokenExpiresAt,
      });
      console.log('👤 사용자 기본 정보:', {
        userEmail: this.userEmail,
        userName: this.userName,
        userGivenName: this.userGivenName,
        userFamilyName: this.userFamilyName,
        userPicture: this.userPicture,
        memId: this.memId,
        userType: this.userType,
      });
      console.log('🎖️ 회원 등급 정보:', {
        memGrade: this.memGrade,
        memGradeName: this.memGradeName,
      });
      console.log('🏢 소속 정보:', {
        deptGroupA: this.deptGroupA,
        deptGroupAName: this.deptGroupAName,
        deptGroupB: this.deptGroupB,
        deptGroupBName: this.deptGroupBName,
        bbdgId: this.bbdgId,
        bbdgIdName: this.bbdgIdName,
      });
      console.log('📊 소속 옵션:', {
        regionOptions: this.regionOptions.length,
        departmentOptions: this.departmentOptions.length,
        branchOptions: this.branchOptions.length,
      });
      console.log('📝 메뉴 정보:', {
        userMenuCount: this.userMenu.length,
        userMenu: this.userMenu,
      });
      console.log('🗺️ view_path별 권한 맵 (전체):', this.authByViewPath);

      // 각 view_path별 권한 정보를 상세하게 출력
      const viewPaths = Object.keys(this.authByViewPath);
      if (viewPaths.length > 0) {
        console.log('📍 view_path별 권한 상세 정보:');
        viewPaths.forEach(path => {
          const authInfo = this.authByViewPath[path];
          console.log(`  🔑 ${path}:`, {
            authOrgUnit: authInfo.authOrgUnit,
            authOrgUnitName: authInfo.authOrgUnitName,
            authDeptGroupA: authInfo.authDeptGroupA,
            authDeptGroupAName: authInfo.authDeptGroupAName,
            authDeptGroupB: authInfo.authDeptGroupB,
            authDeptGroupBName: authInfo.authDeptGroupBName,
            authBbdgId: authInfo.authBbdgId,
            authBbdgIdName: authInfo.authBbdgIdName,
          });
        });
      } else {
        console.log('⚠️ view_path별 권한 정보가 없습니다.');
      }

      console.log('📋 [UserStore] ===========================');
    },
    clearStore() {
      this.isLogin = false;
      this.userToken = '';
      this.refreshToken = '';
      this.accessTokenExpiresAt = '';
      this.refreshTokenExpiresAt = '';
      this.userEmail = '';
      this.userName = '';
      this.userGivenName = '';
      this.userFamilyName = '';
      this.userPicture = '';
      this.userMenu = [];
      this.memId = '';
      this.userType = '';
      this.memGrade = '';
      this.memGradeName = '';
      this.deptGroupA = '';
      this.deptGroupAName = '';
      this.deptGroupB = '';
      this.deptGroupBName = '';
      this.bbdgId = '';
      this.bbdgIdName = '';
      this.regionOptions = [];
      this.departmentOptions = [];
      this.branchOptions = [];
      this.authByViewPath = {};
      if (typeof window !== 'undefined') {
        localStorage.removeItem('access_token');
      }
    },
  },
  persist: {
    key: 'myjungto-user',
    storage: localStorage,
    paths: [
      'isLogin',
      'userToken',
      'refreshToken',
      'accessTokenExpiresAt',
      'refreshTokenExpiresAt',
      'userEmail',
      'userName',
      'memId',
    ],
  },
});
